import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileUp, Trash2, UploadCloud, FileJson, AlertTriangle } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Button } from '@/acc-ui/primitives/Button';
import { Input } from '@/acc-ui/primitives/Input';
import { Badge } from '@/acc-ui/primitives/Badge';
import { ingestionApi, putFileToS3, sha256OfBlob, type CreateLoanFileResponse } from '@/api/ingestion-api';
import { losApi } from '@/api/los-api';
import { LosApplicationDataSchema, APPLICANT_ROLE, type LosApplicationData } from '@loanaudit/shared';
import { cn } from '@/lib/utils';

/**
 * /loan-files/new — upload wizard.
 *
 * Steps:
 *   1. Loan number + LOS file. Borrower / product / branch / officer are
 *      read from the LOS JSON (validated client-side with the same shared
 *      schema the backend uses) — no manual entry.
 *   2. Document picker (drag & drop + file input)
 *   3. Create loan file → upload docs → attach the LOS payload → submit
 *
 * Progress is in-memory; refreshing loses the draft — cheap to recreate.
 */

type FileItem = {
  file: File;
  documentId?: string;
  uploadUrl?: string;
  sha256?: string;
  state: 'idle' | 'uploading' | 'uploaded' | 'registering' | 'registered' | 'error';
  error?: string;
  progress?: number;
};


// Client-side upload guard — mirrors the "PDF, JPG, PNG, TIFF, Word, Excel —
// up to 100 MB each" copy shown to the user. Fail fast before upload.
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.doc,.docx,.xls,.xlsx';
const ALLOWED_EXT = new Set([
  'pdf', 'jpg', 'jpeg', 'png', 'tif', 'tiff', 'doc', 'docx', 'xls', 'xlsx',
]);

// Product code → display label mapping (for nicer dropdown display)
const PRODUCT_LABELS: Record<string, string> = {
  HOME_LOAN: 'Home Loan',
  PL: 'Personal Loan',
  BL: 'Business Loan',
  TL: 'Term Loan',
  WC: 'Working Capital',
  BIZ: 'Business Loan',
  AUTO: 'Auto Loan',
  LAP: 'Loan Against Property',
  DISHA_HL: 'Disha Home Loan',
  MICRO_LAP: 'Micro LAP',
  ML: 'Micro Loan',
};

// Branch code → display label mapping (for nicer dropdown display)
const BRANCH_LABELS: Record<string, string> = {
  MUM_BKC: 'Mumbai — BKC',
  DEL_CP: 'Delhi — Connaught Place',
  BLR_KOR: 'Bangalore — Koramangala',
  CHE_TNG: 'Chennai — T. Nagar',
  KOL_PRK: 'Kolkata — Park Street',
  PUN_KOR: 'Pune — Koregaon Park',
};

// The real LOS API doesn't return the AFL branch code. Never invent one:
// show it as unreported in the UI, and send an unmistakable sentinel to
// ingestion (whose DTO requires a non-empty branch code).
const BRANCH_NOT_REPORTED = 'NOT_REPORTED';

function branchLabel(code: string | null | undefined): string {
  if (!code) return 'Not reported by LOS';
  return BRANCH_LABELS[code] ?? code;
}

export function LoanFileUploadPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 fields
  const [loanNumber, setLoanNumber] = useState('');
  const [losFile, setLosFile] = useState<File | null>(null);
  const [los, setLos] = useState<LosApplicationData | null>(null);
  const [losError, setLosError] = useState<string | null>(null);

  // Step 2 files
  const [files, setFiles] = useState<FileItem[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  // Pipeline state
  const [loanFileId, setLoanFileId] = useState<string | null>(null);
  // The create() response is cached so a failed submit can be retried
  // without creating a duplicate loan file — retry reuses this loanFileId
  // and the presigned upload URLs it returned.
  const [created, setCreated] = useState<CreateLoanFileResponse | null>(null);
  const [submitState, setSubmitState] = useState<
    'idle' | 'creating' | 'uploading' | 'registering' | 'attaching-los' | 'submitting' | 'done' | 'error'
  >('idle');
  const [pipelineError, setPipelineError] = useState<string | null>(null);

  // Fields read from the LOS payload (single source of truth).
  const derived = useMemo(() => {
    if (!los) return null;
    const primary =
      los.applicants.find((a) => a.role === APPLICANT_ROLE.PRIMARY) ?? los.applicants[0];
    return {
      borrowerName: primary?.name ?? '—',
      productCode: los.productCode,
      branchCode: los.branchCode ?? null,
      officerId: los.officerId ?? '',
    };
  }, [los]);

  // Warn if the typed loan number doesn't match the LOS payload's.
  const loanNumberMismatch = useMemo(
    () => !!los && loanNumber.trim() !== '' && loanNumber.trim() !== los.loanNumber,
    [los, loanNumber],
  );

  // Parse + validate a selected LOS .json file with the shared schema.
  const handleLosFile = async (file: File) => {
    setLosFile(file);
    setLos(null);
    setLosError(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const parsed = LosApplicationDataSchema.safeParse(json);
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        setLosError(
          `This doesn't look like a valid LOS file${first ? ` — ${first.path.join('.') || 'root'}: ${first.message}` : ''}.`,
        );
        return;
      }
      setLos(parsed.data);
      // Prefill the loan number from the LOS when the box is empty.
      setLoanNumber((cur) => (cur.trim() === '' ? parsed.data.loanNumber : cur));
    } catch {
      setLosError('Could not read this file as JSON. Upload the LOS export (.json).');
    }
  };

  const step1Valid = useMemo(() => {
    return loanNumber.trim() !== '' && los !== null;
  }, [loanNumber, los]);

  const step2Valid = files.length > 0;

  const addFiles = (incoming: FileList | File[]) => {
    const next: FileItem[] = [];
    const rejected: string[] = [];
    for (const f of Array.from(incoming)) {
      if (files.some((x) => x.file.name === f.name && x.file.size === f.size)) continue;
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ALLOWED_EXT.has(ext)) {
        rejected.push(`${f.name} — unsupported type`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        rejected.push(`${f.name} — over 100 MB`);
        continue;
      }
      next.push({ file: f, state: 'idle' });
    }
    setFileError(rejected.length > 0 ? `Skipped: ${rejected.join('; ')}` : null);
    if (next.length > 0) setFiles([...files, ...next]);
  };

  const removeFile = (idx: number) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };

  const pipeline = useMutation({
    mutationFn: async () => {
      if (!los || !derived) throw new Error('Upload a valid LOS file first.');
      setPipelineError(null);

      // 1. Create loan file — but only once. On retry after a partial
      //    failure, reuse the cached create response (same loanFileId +
      //    presigned URLs) so we don't spawn a duplicate loan file.
      //    ponytail: presigned upload URLs expire; a much-later retry can
      //    fail on upload. Re-fetch URLs per document if that becomes real.
      let create = created;
      if (!create || create.documents.length !== files.length) {
        setSubmitState('creating');
        create = await ingestionApi.create({
          loanNumber: loanNumber.trim(),
          borrowerName: derived.borrowerName,
          productCode: derived.productCode,
          branchCode: derived.branchCode ?? BRANCH_NOT_REPORTED,
          officerId: derived.officerId.trim() || undefined,
          documents: files.map((f) => ({
            originalFileName: f.file.name,
            mimeType: f.file.type || 'application/octet-stream',
            fileSizeBytes: f.file.size,
          })),
        });
        setCreated(create);
        setLoanFileId(create.loanFileId);
      }

      setSubmitState('uploading');

      // 2. Zip DTO docs with local files (order matches)
      const pairs = create.documents.map((d, i) => ({
        doc: d,
        item: files[i]!,
        idx: i,
      }));

      // 3. Upload each to S3 + register. Sequential to keep UI simple.
      //    On retry, skip files that already completed on a prior attempt.
      for (const { doc, item, idx } of pairs) {
        if (item.state === 'registered') continue;
        setFiles((prev) =>
          prev.map((f, i) =>
            i === idx
              ? { ...f, documentId: doc.documentId, uploadUrl: doc.uploadUrl, state: 'uploading' }
              : f,
          ),
        );
        try {
          await putFileToS3(
            doc.uploadUrl,
            item.file,
            item.file.type || 'application/octet-stream',
            (fraction) => {
              setFiles((prev) =>
                prev.map((f, i) =>
                  i === idx ? { ...f, progress: fraction } : f,
                ),
              );
            },
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Upload failed';
          setFiles((prev) =>
            prev.map((f, i) =>
              i === idx ? { ...f, state: 'error', error: msg } : f,
            ),
          );
          throw new Error(`Uploading "${item.file.name}" failed: ${msg}`);
        }
        setFiles((prev) =>
          prev.map((f, i) => (i === idx ? { ...f, state: 'uploaded' } : f)),
        );

        // Compute SHA-256 client-side then register
        const sha = await sha256OfBlob(item.file);
        setFiles((prev) =>
          prev.map((f, i) => (i === idx ? { ...f, sha256: sha, state: 'registering' } : f)),
        );
        try {
          await ingestionApi.registerUploaded(create.loanFileId, doc.documentId, sha);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Registration failed';
          setFiles((prev) =>
            prev.map((f, i) =>
              i === idx ? { ...f, state: 'error', error: msg } : f,
            ),
          );
          throw new Error(`Registering "${item.file.name}" failed: ${msg}`);
        }
        setFiles((prev) =>
          prev.map((f, i) => (i === idx ? { ...f, state: 'registered' } : f)),
        );
      }

      // 4. Attach the LOS payload before submit so the CAM-vs-LOS audit
      //    has it when the pipeline runs. Backend re-validates the payload.
      setSubmitState('attaching-los');
      await losApi.upload(create.loanFileId, los);

      // 5. Submit
      setSubmitState('submitting');
      await ingestionApi.submit(create.loanFileId);
      setSubmitState('done');
      return { loanFileId: create.loanFileId };
    },
    onError: (err) => {
      setPipelineError(err instanceof Error ? err.message : String(err));
      setSubmitState('error');
    },
  });

  return (
    <AppShell crumb="Workspace" title="Upload loan file">
      <PageHeader
        title="Upload loan file"
        subtitle="Enter the loan number and upload the LOS file. Borrower, product and branch are read from the LOS."
        actions={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate('/loan-files')}
          >
            Cancel
          </Button>
        }
      />

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        <StepDot n={1} label="Loan & LOS" active={step === 1} done={step > 1} />
        <StepArrow />
        <StepDot n={2} label="Documents" active={step === 2} done={step > 2} />
        <StepArrow />
        <StepDot n={3} label="Submit" active={step === 3} done={submitState === 'done'} />
      </div>

      {step === 1 && (
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labeled label="Loan number" required>
              <Input
                placeholder="LN2026001"
                value={loanNumber}
                onChange={(e) => setLoanNumber(e.target.value)}
              />
            </Labeled>
          </div>

          {/* LOS upload */}
          <div className="mt-4">
            <SectionLabel>LOS file</SectionLabel>
            <label
              className={cn(
                'mt-2 flex items-center gap-3 rounded-glass-md border-2 border-dashed p-4 cursor-pointer transition-colors',
                losError
                  ? 'border-red-300 bg-red-50/40'
                  : los
                    ? 'border-emerald-300 bg-emerald-50/40'
                    : 'border-glass-hairline bg-white/50 hover:border-brand-blue/40',
              )}
            >
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleLosFile(e.target.files[0])}
              />
              {los ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              ) : (
                <FileJson className="w-6 h-6 text-text-muted shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-label font-medium text-text-primary truncate">
                  {losFile ? losFile.name : 'Upload the LOS export (.json)'}
                </div>
                <div className="text-micro text-text-tertiary">
                  {los
                    ? 'Validated — borrower, product and branch read below.'
                    : 'Borrower, product, branch and officer are read from this file.'}
                </div>
              </div>
              <span className="text-caption text-brand-blue underline shrink-0">
                {losFile ? 'Change' : 'Browse'}
              </span>
            </label>

            {losError && (
              <div className="mt-2 flex items-start gap-1.5 text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{losError}</span>
              </div>
            )}
          </div>

          {/* Derived preview */}
          {derived && (
            <div className="mt-4">
              <SectionLabel>Read from LOS</SectionLabel>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SummaryField label="Borrower" value={derived.borrowerName} />
                <SummaryField label="Product" value={PRODUCT_LABELS[derived.productCode] ?? derived.productCode} />
                <SummaryField label="Branch" value={branchLabel(derived.branchCode)} />
                <SummaryField label="Officer" value={derived.officerId || '—'} />
              </div>
              {loanNumberMismatch && (
                <div className="mt-3 flex items-start gap-1.5 text-caption text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    The loan number you entered doesn't match the LOS file
                    (<span className="font-mono">{los?.loanNumber}</span>). You can proceed, but make sure this is intentional.
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end mt-5">
            <Button disabled={!step1Valid} onClick={() => setStep(2)}>
              Next: Documents
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className={cn(
              'rounded-glass-md border-2 border-dashed border-glass-hairline bg-white/50',
              'p-6 flex flex-col items-center text-center',
            )}
          >
            <UploadCloud className="w-8 h-8 text-text-muted mb-2" />
            <div className="text-label text-text-secondary">
              Drag and drop files here, or{' '}
              <label className="text-brand-blue underline cursor-pointer">
                browse
                <input
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => e.target.files && addFiles(e.target.files)}
                />
              </label>
            </div>
            <div className="mt-1 text-micro text-text-muted">
              PDF, JPG, PNG, TIFF, Word, Excel — up to 100 MB each
            </div>
          </div>

          {fileError && (
            <div className="mt-2 flex items-start gap-1.5 text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{fileError}</span>
            </div>
          )}

          {files.length > 0 && (
            <ul className="mt-4 divide-y divide-glass-hairline border border-glass-hairline rounded-glass-sm">
              {files.map((f, i) => (
                <li key={`${f.file.name}-${i}`} className="flex items-center gap-2 px-3 py-2">
                  <FileUp className="w-4 h-4 text-text-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-label font-medium text-text-primary truncate">
                      {f.file.name}
                    </div>
                    <div className="text-micro text-text-tertiary">
                      {f.file.type || 'unknown'} · {formatBytes(f.file.size)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(i)}
                    leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-between mt-5">
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button disabled={!step2Valid} onClick={() => setStep(3)}>
              Next: Submit
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <SectionLabel>Summary</SectionLabel>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-label">
            <SummaryField label="Loan number" value={loanNumber} />
            <SummaryField label="Borrower" value={derived?.borrowerName ?? '—'} />
            <SummaryField label="Product" value={derived ? PRODUCT_LABELS[derived.productCode] ?? derived.productCode : '—'} />
            <SummaryField label="Branch" value={derived ? branchLabel(derived.branchCode) : '—'} />
            {derived?.officerId && <SummaryField label="Officer" value={derived.officerId} />}
            <SummaryField label="LOS file" value={losFile?.name ?? '—'} />
            <SummaryField label="Documents" value={String(files.length)} />
          </div>

          <div className="mt-4">
            <SectionLabel>Upload progress</SectionLabel>
            <ul className="mt-2 divide-y divide-glass-hairline border border-glass-hairline rounded-glass-sm">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2">
                  <span className="flex-1 min-w-0 text-label text-text-primary truncate">
                    {f.file.name}
                  </span>
                  <FileStateBadge state={f.state} error={f.error} progress={f.progress} />
                </li>
              ))}
            </ul>
          </div>

          {submitState === 'done' && loanFileId && (
            <div className="mt-4 flex items-center gap-2 text-label text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              Submitted. Processing will begin shortly.
            </div>
          )}

          {pipelineError && (
            <div className="mt-4 text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {pipelineError}
            </div>
          )}

          <div className="flex justify-between mt-5">
            <Button
              variant="secondary"
              onClick={() => setStep(2)}
              disabled={submitState !== 'idle' && submitState !== 'error'}
            >
              Back
            </Button>
            {submitState === 'done' && loanFileId ? (
              <Button onClick={() => navigate(`/loan-files/${loanFileId}`)}>
                View loan file
              </Button>
            ) : (
              <Button
                onClick={() => pipeline.mutate()}
                loading={
                  submitState === 'creating' ||
                  submitState === 'uploading' ||
                  submitState === 'registering' ||
                  submitState === 'attaching-los' ||
                  submitState === 'submitting'
                }
                disabled={submitState !== 'idle' && submitState !== 'error'}
              >
                {submitState === 'error' ? 'Retry submit' : 'Upload and submit'}
              </Button>
            )}
          </div>
        </Card>
      )}
    </AppShell>
  );
}

function Labeled({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-caption font-semibold text-text-secondary">
        {label}
        {required && <span className="text-red-700 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-micro font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="text-label text-text-primary mt-0.5 truncate">{value}</div>
    </div>
  );
}

function StepDot({
  n,
  label,
  active,
  done,
}: {
  n: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center text-micro font-semibold',
          done
            ? 'bg-emerald-500/15 text-emerald-700'
            : active
            ? 'bg-brand-blue text-white'
            : 'bg-slate-900/8 text-text-muted',
        )}
      >
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : n}
      </span>
      <span
        className={cn(
          'text-caption font-medium',
          active ? 'text-text-primary' : 'text-text-tertiary',
        )}
      >
        {label}
      </span>
    </div>
  );
}

function StepArrow() {
  return <span className="text-text-muted">›</span>;
}

function FileStateBadge({
  state,
  error,
  progress,
}: {
  state: FileItem['state'];
  error?: string;
  progress?: number;
}) {
  if (state === 'idle') return <Badge tone="muted">queued</Badge>;
  if (state === 'uploading') {
    const pct = progress ? Math.round(progress * 100) : 0;
    return (
      <Badge tone="info" dot>
        uploading {pct}%
      </Badge>
    );
  }
  if (state === 'uploaded') return <Badge tone="info" dot>verifying</Badge>;
  if (state === 'registering') return <Badge tone="info" dot>verifying</Badge>;
  if (state === 'registered') return <Badge tone="success" dot>ready</Badge>;
  return (
    <Badge tone="danger" dot>
      error{error && <span className="ml-1 font-normal opacity-80">· {error.slice(0, 30)}</span>}
    </Badge>
  );
}

function formatBytes(bytes: number): string {
  const MB = 1024 * 1024;
  const KB = 1024;
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${(bytes / KB).toFixed(0)} KB`;
  return `${bytes} B`;
}